import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import { AppContext } from '../../services/context';
import PageHeader from '../../Components/PageHeader';
import request from '../../services/request';
import endPoint from '../../services/endPoint';

/**
 * Interface "Mon Abonnement" pour les utilisateurs finaux
 * Basée sur l'endpoint /api/my-subscription du SubscriptionController
 */
const MySubscription = () => {
  const { user } = useContext(AppContext);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionStats, setSubscriptionStats] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  
  const [cancelForm, setCancelForm] = useState({
    reason: '',
    immediate: false
  });

  useEffect(() => {
    loadMySubscription();
    loadSubscriptionPlans();
    loadAvailableStudents();
  }, []);

  const loadMySubscription = async () => {
    setLoading(true);
    try {
      const response = await request.get(`${endPoint.baseUrl}/my-subscription`);
      if (response.data?.success) {
        setSubscription(response.data.data.subscription);
        setSubscriptionStats(response.data.data.stats);
      } else {
        setSubscription(null);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setSubscription(null);
      } else {
        console.error('Erreur:', error);
        toast.error('Erreur lors du chargement de votre abonnement');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptionPlans = async () => {
    try {
      const response = await request.get(endPoint.subscriptionPlans);
      setSubscriptionPlans(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error('Erreur plans:', error);
    }
  };

  const loadAvailableStudents = async () => {
    try {
      const response = await request.get(`${endPoint.users}?role=student`);
      setStudents(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error('Erreur étudiants:', error);
    }
  };

  const handleAddStudent = async (studentId) => {
    try {
      const response = await request.post(
        `${endPoint.subscriptions}/${subscription.slug}/students`,
        { student_id: studentId }
      );
      if (response.data?.success) {
        toast.success('Élève ajouté avec succès');
        setShowAddStudentModal(false);
        loadMySubscription(); // Rafraîchir les données
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors de l\'ajout');
    }
  };

  const handleRemoveStudent = async (studentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir retirer cet élève ?')) return;

    try {
      const response = await request.delete(
        `${endPoint.subscriptions}/${subscription.slug}/students/${studentId}`
      );
      if (response.data?.success) {
        toast.success('Élève retiré avec succès');
        loadMySubscription(); // Rafraîchir les données
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleRenewSubscription = async () => {
    try {
      const response = await request.post(`${endPoint.subscriptions}/${subscription.slug}/renew`);
      if (response.data?.success) {
        toast.success('Abonnement renouvelé avec succès');
        loadMySubscription();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors du renouvellement');
    }
  };

  const handleCancelSubscription = async (e) => {
    e.preventDefault();
    try {
      const response = await request.post(
        `${endPoint.subscriptions}/${subscription.slug}/cancel`,
        cancelForm
      );
      if (response.data?.success) {
        toast.success('Demande d\'annulation envoyée');
        setShowCancelModal(false);
        setCancelForm({ reason: '', immediate: false });
        loadMySubscription();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors de l\'annulation');
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: 'bg-success',
      trial: 'bg-info',
      expired: 'bg-warning',
      cancelled: 'bg-danger',
      suspended: 'bg-secondary'
    };

    const statusLabels = {
      active: 'Actif',
      trial: 'Période d\'essai',
      expired: 'Expiré',
      cancelled: 'Annulé',
      suspended: 'Suspendu'
    };

    return (
      <span className={`badge ${statusClasses[status] || 'bg-secondary'}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non défini';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPrice = (price, currency = 'XOF') => {
    return new Intl.NumberFormat('fr-FR').format(price) + ' ' + currency;
  };

  const getRemainingDays = (endsAt) => {
    if (!endsAt) return null;
    const now = new Date();
    const endDate = new Date(endsAt);
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getUsagePercentage = () => {
    if (!subscription || !subscription.subscription_plan) return 0;
    const maxStudents = subscription.subscription_plan.max_students;
    const currentStudents = subscription.students?.length || 0;
    
    if (maxStudents === -1) return 0; // Illimité
    return Math.round((currentStudents / maxStudents) * 100);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{minHeight: '400px'}}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="container-fluid">
        <PageHeader title="Mon Abonnement" />
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="card">
              <div className="card-body text-center py-5">
                <i className="bi bi-exclamation-circle display-1 text-warning"></i>
                <h3 className="mt-3">Aucun abonnement actif</h3>
                <p className="text-muted">
                  Vous n'avez actuellement aucun abonnement actif à la plateforme WILOFO.
                </p>
                <div className="mt-4">
                  <button className="btn btn-primary btn-lg" onClick={() => window.location.href = '/subscription-plans'}>
                    <i className="bi bi-plus-circle me-2"></i>
                    Choisir un Abonnement
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const remainingDays = getRemainingDays(subscription.ends_at);
  const usagePercentage = getUsagePercentage();
  const isExpiringSoon = remainingDays !== null && remainingDays <= 7 && remainingDays > 0;
  const isExpired = remainingDays !== null && remainingDays <= 0;

  return (
    <div className="container-fluid">
      <PageHeader title="Mon Abonnement" />

      {/* Alerts */}
      {isExpiringSoon && (
        <div className="alert alert-warning alert-dismissible fade show mb-4">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>Attention!</strong> Votre abonnement expire dans {remainingDays} jour{remainingDays !== 1 ? 's' : ''}. 
          <button 
            className="btn btn-sm btn-warning ms-3"
            onClick={handleRenewSubscription}
          >
            Renouveler maintenant
          </button>
          <button type="button" className="btn-close" data-bs-dismiss="alert"></button>
        </div>
      )}

      {isExpired && (
        <div className="alert alert-danger alert-dismissible fade show mb-4">
          <i className="bi bi-x-circle me-2"></i>
          <strong>Abonnement expiré!</strong> Votre abonnement a expiré le {formatDate(subscription.ends_at)}.
          <button 
            className="btn btn-sm btn-danger ms-3"
            onClick={handleRenewSubscription}
          >
            Renouveler
          </button>
          <button type="button" className="btn-close" data-bs-dismiss="alert"></button>
        </div>
      )}

      {subscription.status === 'trial' && subscription.trial_ends_at && (
        <div className="alert alert-info alert-dismissible fade show mb-4">
          <i className="bi bi-info-circle me-2"></i>
          <strong>Période d'essai:</strong> Votre essai gratuit se termine le {formatDate(subscription.trial_ends_at)}.
          <button type="button" className="btn-close" data-bs-dismiss="alert"></button>
        </div>
      )}

      <div className="row">
        {/* Informations Principales */}
        <div className="col-lg-8">
          <div className="card mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="bi bi-credit-card me-2"></i>
                Détails de l'Abonnement
              </h5>
              {getStatusBadge(subscription.status)}
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <table className="table table-borderless">
                    <tbody>
                      <tr>
                        <th width="40%">Plan:</th>
                        <td>
                          <strong>{subscription.subscription_plan?.name}</strong>
                          <br />
                          <small className="text-muted">
                            {subscription.subscription_plan?.description}
                          </small>
                        </td>
                      </tr>
                      <tr>
                        <th>Tarif:</th>
                        <td>
                          <strong className="text-primary">
                            {formatPrice(subscription.subscription_plan?.price, subscription.currency)}
                          </strong>
                          <span className="text-muted">
                            /{subscription.subscription_plan?.billing_cycle === 'monthly' ? 'mois' : 
                              subscription.subscription_plan?.billing_cycle === 'yearly' ? 'an' : 
                              'trimestre'}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <th>Période:</th>
                        <td>
                          Du {formatDate(subscription.starts_at)} au {formatDate(subscription.ends_at)}
                        </td>
                      </tr>
                      {subscription.trial_ends_at && (
                        <tr>
                          <th>Fin d'essai:</th>
                          <td className="text-info">
                            {formatDate(subscription.trial_ends_at)}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="col-md-6">
                  <table className="table table-borderless">
                    <tbody>
                      <tr>
                        <th width="50%">Montant payé:</th>
                        <td>
                          <strong className="text-success">
                            {formatPrice(subscription.amount_paid, subscription.currency)}
                          </strong>
                        </td>
                      </tr>
                      <tr>
                        <th>Renouvellement auto:</th>
                        <td>
                          <span className={`badge ${subscription.auto_renew ? 'bg-success' : 'bg-secondary'}`}>
                            {subscription.auto_renew ? 'Activé' : 'Désactivé'}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <th>Temps restant:</th>
                        <td>
                          {remainingDays !== null ? (
                            <span className={`badge ${remainingDays <= 7 ? 'bg-warning' : remainingDays <= 0 ? 'bg-danger' : 'bg-success'}`}>
                              {remainingDays > 0 ? `${remainingDays} jours` : 'Expiré'}
                            </span>
                          ) : (
                            <span className="text-muted">Indéterminé</span>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th>Créé le:</th>
                        <td>{formatDate(subscription.created_at)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Gestion des Élèves */}
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-0">
                  <i className="bi bi-people me-2"></i>
                  Mes Élèves ({subscription.students?.length || 0})
                </h5>
                {subscription.subscription_plan?.max_students !== -1 && (
                  <small className="text-muted">
                    Maximum autorisé: {subscription.subscription_plan?.max_students}
                  </small>
                )}
              </div>
              {subscription.status === 'active' && 
               (subscription.subscription_plan?.max_students === -1 || 
                (subscription.students?.length || 0) < subscription.subscription_plan?.max_students) && (
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setShowAddStudentModal(true)}
                >
                  <i className="bi bi-person-plus me-1"></i>
                  Ajouter un élève
                </button>
              )}
            </div>
            <div className="card-body">
              {/* Barre d'usage */}
              {subscription.subscription_plan?.max_students !== -1 && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <small>Utilisation</small>
                    <small>{usagePercentage}%</small>
                  </div>
                  <div className="progress" style={{height: '8px'}}>
                    <div 
                      className={`progress-bar ${
                        usagePercentage >= 90 ? 'bg-danger' : 
                        usagePercentage >= 75 ? 'bg-warning' : 'bg-success'
                      }`}
                      style={{width: `${usagePercentage}%`}}
                    />
                  </div>
                </div>
              )}

              {/* Liste des élèves */}
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Email</th>
                      <th>Ajouté le</th>
                      <th width="100">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscription.students && subscription.students.length > 0 ? (
                      subscription.students.map(subscriptionStudent => (
                        <tr key={subscriptionStudent.id}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="avatar-sm bg-primary rounded-circle d-flex align-items-center justify-content-center me-2">
                                <i className="bi bi-person text-white"></i>
                              </div>
                              <strong>{subscriptionStudent.student?.name}</strong>
                            </div>
                          </td>
                          <td>{subscriptionStudent.student?.email}</td>
                          <td>{formatDate(subscriptionStudent.created_at)}</td>
                          <td>
                            {subscription.status === 'active' && (
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleRemoveStudent(subscriptionStudent.student?.id)}
                                title="Retirer l'élève"
                              >
                                <i className="bi bi-person-dash"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center py-4">
                          <i className="bi bi-people display-4 text-muted"></i>
                          <p className="text-muted mt-2">Aucun élève inscrit</p>
                          {subscription.status === 'active' && (
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => setShowAddStudentModal(true)}
                            >
                              <i className="bi bi-person-plus me-1"></i>
                              Ajouter votre premier élève
                            </button>
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar Actions */}
        <div className="col-lg-4">
          {/* Statistiques */}
          {subscriptionStats && (
            <div className="card mb-4">
              <div className="card-header">
                <h6 className="mb-0">
                  <i className="bi bi-graph-up me-2"></i>
                  Statistiques
                </h6>
              </div>
              <div className="card-body">
                <div className="row text-center">
                  <div className="col-6">
                    <div className="border-end">
                      <h4 className="text-primary mb-1">{subscriptionStats.students_count || 0}</h4>
                      <small className="text-muted">Élèves</small>
                    </div>
                  </div>
                  <div className="col-6">
                    <h4 className="text-success mb-1">{subscriptionStats.days_remaining || 0}</h4>
                    <small className="text-muted">Jours restants</small>
                  </div>
                </div>
                
                {subscriptionStats.usage_stats && (
                  <div className="mt-3">
                    <div className="d-flex justify-content-between">
                      <small>Cours consultés:</small>
                      <strong>{subscriptionStats.usage_stats.courses_accessed || 0}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <small>Évaluations:</small>
                      <strong>{subscriptionStats.usage_stats.evaluations_completed || 0}</strong>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions Rapides */}
          <div className="card mb-4">
            <div className="card-header">
              <h6 className="mb-0">
                <i className="bi bi-lightning me-2"></i>
                Actions Rapides
              </h6>
            </div>
            <div className="card-body">
              <div className="d-grid gap-2">
                {subscription.status === 'active' && (
                  <>
                    <button
                      className="btn btn-success"
                      onClick={handleRenewSubscription}
                    >
                      <i className="bi bi-arrow-repeat me-2"></i>
                      Renouveler
                    </button>
                    <button
                      className="btn btn-warning"
                      onClick={() => setShowUpgradeModal(true)}
                    >
                      <i className="bi bi-arrow-up-circle me-2"></i>
                      Changer de plan
                    </button>
                  </>
                )}

                {subscription.status === 'expired' && (
                  <button
                    className="btn btn-primary"
                    onClick={handleRenewSubscription}
                  >
                    <i className="bi bi-arrow-repeat me-2"></i>
                    Réactiver
                  </button>
                )}

                {['active', 'trial'].includes(subscription.status) && (
                  <button
                    className="btn btn-outline-danger"
                    onClick={() => setShowCancelModal(true)}
                  >
                    <i className="bi bi-x-circle me-2"></i>
                    Annuler
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Fonctionnalités du Plan */}
          <div className="card">
            <div className="card-header">
              <h6 className="mb-0">
                <i className="bi bi-star me-2"></i>
                Fonctionnalités Incluses
              </h6>
            </div>
            <div className="card-body">
              <div className="list-group list-group-flush">
                {subscription.subscription_plan?.analytics_access && (
                  <div className="list-group-item border-0 px-0 py-2">
                    <i className="bi bi-check-circle text-success me-2"></i>
                    Accès aux analyses
                  </div>
                )}
                
                {subscription.subscription_plan?.priority_support && (
                  <div className="list-group-item border-0 px-0 py-2">
                    <i className="bi bi-check-circle text-success me-2"></i>
                    Support prioritaire
                  </div>
                )}

                {subscription.subscription_plan?.offline_access && (
                  <div className="list-group-item border-0 px-0 py-2">
                    <i className="bi bi-check-circle text-success me-2"></i>
                    Accès hors ligne
                  </div>
                )}

                {subscription.subscription_plan?.custom_branding && (
                  <div className="list-group-item border-0 px-0 py-2">
                    <i className="bi bi-check-circle text-success me-2"></i>
                    Personnalisation
                  </div>
                )}

                <div className="list-group-item border-0 px-0 py-2">
                  <i className="bi bi-people text-info me-2"></i>
                  {subscription.subscription_plan?.max_students === -1 ? 
                    'Élèves illimités' : 
                    `Jusqu'à ${subscription.subscription_plan?.max_students} élèves`
                  }
                </div>

                <div className="list-group-item border-0 px-0 py-2">
                  <i className="bi bi-book text-info me-2"></i>
                  {subscription.subscription_plan?.max_classes === -1 ? 
                    'Classes illimitées' : 
                    `${subscription.subscription_plan?.max_classes} classes`
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Ajouter Élève */}
      {showAddStudentModal && (
        <div className="modal fade show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Ajouter un Élève</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAddStudentModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Sélectionner un élève</label>
                  <select className="form-select" id="studentSelect">
                    <option value="">Choisir un élève...</option>
                    {students.filter(student => 
                      !subscription.students?.some(ss => ss.student?.id === student.id)
                    ).map(student => (
                      <option key={student.id} value={student.id}>
                        {student.name} ({student.email})
                      </option>
                    ))}
                  </select>
                  {students.length === 0 && (
                    <div className="form-text text-warning">
                      Aucun élève disponible. Veuillez d'abord créer des comptes d'élèves.
                    </div>
                  )}
                </div>

                {subscription.subscription_plan?.max_students !== -1 && (
                  <div className="alert alert-info">
                    <small>
                      <i className="bi bi-info-circle me-1"></i>
                      Vous pouvez ajouter {subscription.subscription_plan.max_students - (subscription.students?.length || 0)} élève(s) supplémentaire(s).
                    </small>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddStudentModal(false)}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    const select = document.getElementById('studentSelect');
                    const studentId = select.value;
                    if (studentId) {
                      handleAddStudent(studentId);
                    } else {
                      toast.error('Veuillez sélectionner un élève');
                    }
                  }}
                >
                  <i className="bi bi-person-plus me-1"></i>
                  Ajouter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'Annulation */}
      {showCancelModal && (
        <div className="modal fade show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-danger">Annuler mon Abonnement</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCancelModal(false);
                    setCancelForm({ reason: '', immediate: false });
                  }}
                ></button>
              </div>
              <form onSubmit={handleCancelSubscription}>
                <div className="modal-body">
                  <div className="alert alert-warning">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Nous regrettons que vous souhaitiez annuler votre abonnement.
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Dites-nous pourquoi vous annulez</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={cancelForm.reason}
                      onChange={(e) => setCancelForm({...cancelForm, reason: e.target.value})}
                      placeholder="Votre feedback nous aidera à améliorer nos services..."
                      maxLength="500"
                    />
                    <div className="form-text">{cancelForm.reason.length}/500 caractères</div>
                  </div>

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="immediate"
                      checked={cancelForm.immediate}
                      onChange={(e) => setCancelForm({...cancelForm, immediate: e.target.checked})}
                    />
                    <label className="form-check-label" htmlFor="immediate">
                      Annulation immédiate (sinon à la fin de la période payée)
                    </label>
                  </div>

                  <div className="mt-3">
                    <small className="text-muted">
                      <i className="bi bi-info-circle me-1"></i>
                      En cas d'annulation immédiate, vous perdrez l'accès aux fonctionnalités premium instantanément.
                    </small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowCancelModal(false);
                      setCancelForm({ reason: '', immediate: false });
                    }}
                  >
                    Finalement, non
                  </button>
                  <button type="submit" className="btn btn-danger">
                    <i className="bi bi-x-circle me-1"></i>
                    Confirmer l'Annulation
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MySubscription;
